import { useState } from 'preact/hooks';
import { useStore, type View } from '../lib/store';
import type { Message } from '../lib/types';

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function StatusBadge({ status }: { status: string }) {
  if (!status || status === 'parsed') return null;
  if (status === 'failed')
    return <span class="msg-badge failed">Failed to load</span>;
  if (status === 'rejected')
    return <span class="msg-badge rejected">Blocked</span>;
  if (['received', 'stored_raw', 'queued', 'parsing'].includes(status))
    return <span class="msg-badge processing">Processing</span>;
  return null;
}

interface ThreadGroup {
  key: string;
  messages: Message[];
}

function groupByThread(list: Message[]): ThreadGroup[] {
  const map = new Map<string, Message[]>();
  for (const m of list) {
    const key = m.threadId || m.id;
    let bucket = map.get(key);
    if (!bucket) {
      bucket = [];
      map.set(key, bucket);
    }
    bucket.push(m);
  }
  const groups: ThreadGroup[] = [];
  for (const [key, msgs] of map) {
    msgs.sort((a, b) => b.receivedAt - a.receivedAt);
    groups.push({ key, messages: msgs });
  }
  groups.sort((a, b) => b.messages[0].receivedAt - a.messages[0].receivedAt);
  return groups;
}

interface MessageListProps {
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onCopyAddress: () => void;
}

export function MessageList({
  loading,
  error,
  onRetry,
  onCopyAddress,
}: MessageListProps) {
  const {
    messages,
    sentMail,
    currentView,
    currentMessageId,
    setCurrentMessageId,
    composeOpen,
    setComposeOpen,
    searchQuery,
    labels,
    activeLabel,
    setActiveLabel,
  } = useStore();

  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());

  const isSent = currentView === 'sent';

  const labelById = new Map(labels.map((l) => [l.id, l]));

  let displayMessages = messages;

  if (!isSent && activeLabel != null) {
    displayMessages = displayMessages.filter((m) =>
      (m.labelIds || []).includes(activeLabel),
    );
  }

  if (!isSent && searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    displayMessages = displayMessages.filter((m) => {
      const from = (m.fromName || m.fromAddr || '').toLowerCase();
      const subject = (m.subject || '').toLowerCase();
      const snippet = (m.snippet || '').toLowerCase();
      return from.includes(q) || subject.includes(q) || snippet.includes(q);
    });
  }

  const hasLabelFilter = !isSent && (labels.length > 0 || activeLabel != null);

  if (loading) {
    return (
      <div class="sidebar-content">
        <p style="font-size:13px;color:var(--muted);padding:16px;text-align:center">Loading messages…</p>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} class="msg-skeleton">
            <div class="skel-line medium" />
            <div class="skel-line short" />
            <div class="skel-line long" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div class="error-retry">
        <p>Failed to load {isSent ? 'sent mail' : 'messages'}</p>
        <button class="btn btn-ghost btn-sm" onClick={onRetry}>
          Try Again
        </button>
      </div>
    );
  }

  if (isSent) {
    if (sentMail.length === 0) {
      return (
        <div class="empty-sidebar" data-testid="sent-empty">
          <p>No sent emails yet</p>
          <p style="font-size:12px">Click Compose to write your first email.</p>
        </div>
      );
    }
    return (
      <div
        class="sidebar-content"
        role="list"
        aria-label="Sent messages"
      >
        {sentMail.map((s) => (
          <div key={s.to_addr + s.created_at} class="msg-item" role="listitem" data-testid="message-row">
            <div class="msg-from">To: {s.to_addr}</div>
            <div class="msg-subject">{s.subject || '(no subject)'}</div>
            <div class="msg-meta">
              <span class="msg-time">{formatTime(s.created_at)}</span>
              <span class="sent-badge">{s.status}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (displayMessages.length === 0) {
    return (
      <div class="empty-sidebar" data-testid="inbox-empty">
        <p>Your inbox is empty</p>
        <p style="font-size:12px">Share your address to start receiving mail.</p>
        <button
          class="btn btn-ghost btn-sm"
          style="margin-top:8px"
          onClick={onCopyAddress}
        >
          Copy Address
        </button>
      </div>
    );
  }

  const groups = groupByThread(displayMessages);

  const openMessage = (m: Message) => {
    setCurrentMessageId(m.id);
    if (!composeOpen) {
      const newHash = '#' + currentView + '/' + m.id;
      if (location.hash !== newHash) history.pushState(null, '', newHash);
    }
  };

  const toggleExpand = (key: string) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div
      class="sidebar-content"
      role="list"
      aria-label="Inbox messages"
    >
      {hasLabelFilter && (
        <div class="label-filter-bar">
          <select
            class="label-filter-select"
            value={activeLabel == null ? '' : String(activeLabel)}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              setActiveLabel(v ? Number(v) : null);
            }}
            aria-label="Filter by label"
            data-testid="label-filter"
          >
            <option value="">All labels</option>
            {labels.map((l) => (
              <option key={l.id} value={String(l.id)}>{l.name}</option>
            ))}
          </select>
        </div>
      )}
      {groups.map((group) => (
        <ThreadRow
          key={group.key}
          group={group}
          currentMessageId={currentMessageId}
          expanded={expandedThreads.has(group.key)}
          onToggleExpand={() => toggleExpand(group.key)}
          onOpenMessage={openMessage}
          labelById={labelById}
        />
      ))}
    </div>
  );
}

interface ThreadRowProps {
  group: ThreadGroup;
  currentMessageId: string | null;
  expanded: boolean;
  onToggleExpand: () => void;
  onOpenMessage: (m: Message) => void;
  labelById: Map<number, { id: number; name: string; color: string | null }>;
}

function ThreadRow({
  group,
  currentMessageId,
  expanded,
  onToggleExpand,
  onOpenMessage,
  labelById,
}: ThreadRowProps) {
  const latest = group.messages[0];
  const count = group.messages.length;
  const hasThread = count > 1;
  const anyUnread = group.messages.some((m) => !m.isRead);
  const anyActive = group.messages.some((m) => m.id === currentMessageId);

  const rowLabels = (latest.labelIds || [])
    .map((id) => labelById.get(id))
    .filter((l): l is { id: number; name: string; color: string | null } => !!l)
    .slice(0, 3);

  return (
    <div
      class={'msg-item' + (anyUnread ? ' unread' : '') + (anyActive ? ' active' : '')}
      role="listitem"
      tabindex={0}
      onClick={() => onOpenMessage(latest)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpenMessage(latest);
      }}
      data-testid="message-row"
    >
      <div class="msg-from">{latest.fromName || latest.fromAddr || 'Unknown'}</div>
      <div class="msg-subject">
        {latest.subject || '(no subject)'}
        {rowLabels.length > 0 && (
          <span class="msg-label-row">
            {rowLabels.map((l) => (
              <span
                key={l.id}
                class="label-pill"
                style={l.color ? { backgroundColor: l.color } : undefined}
                data-testid="message-label-badge"
              >
                {l.name}
              </span>
            ))}
          </span>
        )}
      </div>
      {latest.snippet && <div class="msg-snippet">{latest.snippet}</div>}
      <div class="msg-meta">
        <span class="msg-time">{formatTime(latest.receivedAt)}</span>
        <span class="msg-meta-right">
          {hasThread && (
            <button
              type="button"
              class={'msg-thread-badge' + (expanded ? ' expanded' : '')}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
              aria-label={`${count} messages in thread`}
              aria-expanded={expanded}
              data-testid="thread-toggle"
            >
              <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
              {'\u21A9 '}
              {count}
            </button>
          )}
          <StatusBadge status={latest.ingestStatus} />
        </span>
      </div>

      {hasThread && expanded && (
        <div class="thread-children" role="list" onClick={(e) => e.stopPropagation()}>
          {group.messages.map((m) => (
            <div
              key={m.id}
              class={'thread-child' + (m.id === currentMessageId ? ' current' : '') + (m.isRead ? '' : ' unread')}
              role="listitem"
              tabindex={0}
              onClick={() => onOpenMessage(m)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onOpenMessage(m);
              }}
              data-testid="thread-child-row"
            >
              <span class="thread-child-from">{m.fromName || m.fromAddr || 'Unknown'}</span>
              <span class="thread-child-subject">{m.subject || '(no subject)'}</span>
              <span class="thread-child-time">{formatTime(m.receivedAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export type { View };
